/**
 * Event Helpers - 事件標籤和顏色輔助方法
 */

import type {
  EventType,
  EventPriority,
  EventSource,
  IncidentLevel,
  IncidentStatus,
  RiskType,
  RiskLevel
} from './types'

import {
  EVENT_TYPES,
  EVENT_PRIORITIES,
  EVENT_SOURCES,
  INCIDENT_LEVELS,
  INCIDENT_STATUSES,
  RISK_TYPES,
  RISK_LEVELS
} from './constants'

// ==================== 標籤方法 ====================

/**
 * 取得事件類型標籤
 */
export function getEventTypeLabel(type: EventType): string {
  const typeLabels: Record<EventType, string> = {
    alarm: '警告',
    warning: '警示',
    info: '資訊',
    system: '系統',
    maintenance: '維護',
    temperature: '溫度',
    condition: '條件事件',
    timeout: '通訊逾時',
    recovery: '條件事件恢復',
    other: '其他',
    incident: '應急',
    risk: '風險'
  }
  return typeLabels[type] || type
}

/**
 * 取得優先級標籤
 */
export function getPriorityLabel(priority: EventPriority): string {
  const priorityLabels: Record<EventPriority, string> = {
    high: '高',
    medium: '中',
    low: '低'
  }
  return priorityLabels[priority] || priority
}

/**
 * 取得來源標籤
 */
export function getSourceLabel(source: EventSource): string {
  const sourceLabels: Record<EventSource, string> = {
    device: '設備',
    system: '系統',
    user: '使用者',
    mqtt: 'MQTT',
    api: 'API'
  }
  return sourceLabels[source] || source
}

/**
 * 取得緊急事件等級標籤
 */
export function getIncidentLevelLabel(level: IncidentLevel): string {
  const levelLabels: Record<IncidentLevel, string> = {
    critical: '緊急',
    high: '高',
    medium: '中'
  }
  return levelLabels[level] || level
}

/**
 * 取得緊急事件狀態標籤
 */
export function getIncidentStatusLabel(status: IncidentStatus): string {
  const statusLabels: Record<IncidentStatus, string> = {
    active: '進行中',
    pending: '待確認',
    processing: '處理中',
    resolved: '已解決'
  }
  return statusLabels[status] || status
}

/**
 * 取得風險類型標籤
 */
export function getRiskTypeLabel(riskType: RiskType): string {
  const riskTypeLabels: Record<RiskType, string> = {
    overload: '用電超約',
    generation: '發電不足',
    weather: '天候風險',
    equipment: '設備異常'
  }
  return riskTypeLabels[riskType] || riskType
}

/**
 * 取得風險等級標籤
 */
export function getRiskLevelLabel(level: RiskLevel): string {
  const levelLabels: Record<RiskLevel, string> = {
    high: '高',
    medium: '中',
    low: '低'
  }
  return levelLabels[level] || level
}

// ==================== 顏色方法 ====================

type ColorName = 'red' | 'orange' | 'blue' | 'purple' | 'green' | 'volcano' | 'default'

/**
 * 取得事件類型顏色
 */
export function getEventTypeColor(type: EventType): ColorName {
  const typeColors: Partial<Record<EventType, ColorName>> = {
    alarm: 'red',
    warning: 'orange',
    info: 'blue',
    system: 'purple',
    maintenance: 'green',
    temperature: 'orange',
    condition: 'orange',
    timeout: 'red',
    incident: 'red',
    risk: 'volcano'
  }
  return typeColors[type] || 'default'
}

/**
 * 取得優先級顏色
 */
export function getPriorityColor(priority: EventPriority): ColorName {
  const priorityColors: Record<EventPriority, ColorName> = {
    high: 'red',
    medium: 'orange',
    low: 'green'
  }
  return priorityColors[priority] || 'default'
}
